import { useTranslation } from 'react-i18next';

import { useMatches, useParams, useRouteLoaderData } from '@remix-run/react';

import { isCommunitySpaceRoute } from '~/constants/community';
import { useEnsureCsrfToken } from '~/context/CsrfContext';
import { NectarPageBlock } from '~/utils/nectarPageBlock';

import type { loader as communityLayoutLoader } from '../../community';
import { useCommunityMembership } from './useCommunityMembership';
import { useSpaceMembership } from './useSpaceMembership';
import { SPACE_MEMBERSHIP_ACTION, useSpaceMembershipFetcher } from './useSpaceMembershipFetcher';

export interface Space {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  read_only: boolean;
  gated: boolean;
  joined: boolean;
  isPending: boolean;
  onToggle: () => void;
}

/**
 * Returns the current space (from the URL) with its membership state and
 * toggle action. Null when not on a space route or when the slug is unknown.
 * `onToggle` opens the community join modal for non-members; for members it
 * submits the space_membership fetcher.
 */
export const useSpace = (): Space | null => {
  const { t } = useTranslation();
  const ensureCsrfToken = useEnsureCsrfToken();
  const matches = useMatches();
  const { slug } = useParams();
  const parentData = useRouteLoaderData<typeof communityLayoutLoader>('routes/community');
  const { isMember, openJoinModal } = useCommunityMembership();
  const { isJoined } = useSpaceMembership();
  const { fetcher } = useSpaceMembershipFetcher();

  const isSpaceRoute = matches.some((m) => isCommunitySpaceRoute(m.id));
  if (!isSpaceRoute || !slug) return null;

  const space = parentData?.spaces?.find((s) => s.slug === slug);
  if (!space) return null;

  const joined = isJoined(slug);
  const isPending = fetcher.state !== 'idle';

  const onToggle = async () => {
    if (!isMember) {
      openJoinModal(NectarPageBlock.CommunitySidebarChannel);
      return;
    }
    fetcher.submit(
      { space_slug: slug, action_type: joined ? 'leave' : 'join', csrf: await ensureCsrfToken() },
      { method: 'POST', action: SPACE_MEMBERSHIP_ACTION },
    );
  };

  return {
    id: space.id,
    slug,
    name: space.name ?? t('community.space_name_fallback'),
    description: space.description,
    read_only: space.read_only,
    gated: space.gated,
    joined,
    isPending,
    onToggle,
  };
};
